import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { 
  BookOpen, 
  Search, 
  Filter, 
  Star,
  TrendingUp,
  Zap,
  Cloud,
  Target,
  Lightbulb,
  Clock,
  Award,
  Shield,
  Heart,
  Coins,
  Leaf,
  Sparkles,
  ArrowUpRight,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { cn } from '../lib/utils'
import { gameGuides, quickTips, weatherEffects, rarityValues } from '../lib/gameGuide'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

const categories = [
  { id: 'all', name: 'All', icon: BookOpen, color: 'text-foreground', bgColor: 'bg-muted/50', borderColor: 'border-border/30' },
  { id: 'beginner', name: 'Beginner', icon: Target, color: 'text-success', bgColor: 'bg-success/10', borderColor: 'border-success/30' },
  { id: 'advanced', name: 'Advanced', icon: TrendingUp, color: 'text-info', bgColor: 'bg-info/10', borderColor: 'border-info/30' },
  { id: 'weather', name: 'Weather', icon: Cloud, color: 'text-purple', bgColor: 'bg-purple/10', borderColor: 'border-purple/30' },
  { id: 'economy', name: 'Economy', icon: Coins, color: 'text-orange', bgColor: 'bg-orange/10', borderColor: 'border-orange/30' },
  { id: 'strategy', name: 'Strategy', icon: Lightbulb, color: 'text-pink', bgColor: 'bg-pink/10', borderColor: 'border-pink/30' }
]

export default function GameGuide() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [expandedGuide, setExpandedGuide] = useState<string | null>(null)

  const filteredGuides = useMemo(() => {
    return gameGuides.filter(guide => {
      const matchesSearch = guide.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           guide.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           guide.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      const matchesCategory = selectedCategory === 'all' || guide.category === selectedCategory
      return matchesSearch && matchesCategory
    })
  }, [searchTerm, selectedCategory])

  const toggleGuide = (guideTitle: string) => {
    setExpandedGuide(expandedGuide === guideTitle ? null : guideTitle)
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-modern"
    >
      <div className="space-modern-sm">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-3 rounded-2xl bg-gradient-to-r from-orange to-orange/80 shadow-lg">
            <BookOpen className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
                        <h1 className="text-3xl font-bold tracking-tight text-foreground">
              Game Guide
            </h1>
        <p className="text-muted-foreground leading-relaxed">
          Comprehensive guides, tips, and strategies to master Grow a Garden
        </p>
          </div>
        </div>
      </div>

      {/* Enhanced Search and Filters */}
      <motion.div variants={itemVariants} className="gaming-card">
        <div className="card-header">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-purple/20 to-purple/10 border border-purple/30">
              <Filter className="h-6 w-6 text-purple" />
            </div>
            <h3 className="card-title">Search & Filter</h3>
          </div>
        </div>
        <div className="card-content">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Enhanced Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search guides, tips, and strategies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-12"
                />
              </div>
            </div>

            {/* Enhanced Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <motion.button
                  key={category.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedCategory(category.id)}
                  className={cn(
                    "btn btn-sm flex items-center",
                    selectedCategory === category.id ? "btn-primary" : "btn-outline"
                  )}
                >
                  <div className={cn("w-2 h-2 rounded-full mr-2", category.bgColor, category.borderColor, "border")} />
                  {category.name}
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Enhanced Quick Tips */}
      <motion.div variants={itemVariants} className="gaming-card">
        <div className="card-header">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-info/20 to-info/10 border border-info/30">
              <Zap className="h-6 w-6 text-info" />
            </div>
            <h3 className="card-title">Quick Tips</h3>
          </div>
        </div>
        <div className="card-content">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {quickTips.slice(0, 6).map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="p-4 glass rounded-xl border border-border/30 transition-modern hover:bg-muted/20"
              >
                <div className="flex items-start space-x-3">
                  <div className="p-2 rounded-lg bg-info/10 border border-info/30">
                    <Lightbulb className="h-4 w-4 text-info" />
                  </div>
                  <p className="text-sm leading-relaxed">{tip}</p>
                </div>
              </motion.div>
            ))}
        </div>
      </div>
      </motion.div>

      {/* Enhanced Game Guides */}
      <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-success/20 to-success/10 border border-success/30">
                <BookOpen className="h-6 w-6 text-success" />
              </div>
              <h3 className="card-title">Game Guides ({filteredGuides.length})</h3>
            </div>
            </div>
          </div>
          <div className="card-content">
              <div className="space-y-4">
            {filteredGuides.map((guide, index) => {
              const isExpanded = expandedGuide === guide.title
              const category = categories.find(c => c.id === guide.category)
              
              return (
                <motion.div
                  key={guide.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass rounded-xl border border-border/30 overflow-hidden transition-modern hover:bg-muted/20"
                >
                  <motion.button
                    onClick={() => toggleGuide(guide.title)}
                    className="w-full p-4 text-left"
                    whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={cn("p-2 rounded-lg border", category?.bgColor, category?.borderColor)}>
                          {category?.icon && <category.icon className={cn("h-5 w-5", category.color)} />}
                        </div>
                        <div>
                          <h4 className="font-semibold text-lg">{guide.title}</h4>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={cn(
                              "text-xs font-medium px-2 py-1 rounded-lg border border-border/30",
                              category?.bgColor,
                              category?.borderColor
                            )}>
                              {category?.name}
                            </span>
                            {guide.tags.slice(0, 2).map((tag, tagIndex) => (
                              <span key={tagIndex} className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-lg">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                          </div>
                      <div className="flex items-center space-x-2">
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </motion.button>
                  
                  <motion.div
                    initial={false}
                    animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 border-t border-border/30">
                      <div className="prose prose-invert max-w-none">
                        <div 
                          className="text-sm leading-relaxed text-muted-foreground mb-4" 
                          dangerouslySetInnerHTML={{ __html: guide.content }} 
                        />
                        {guide.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {guide.tags.map((tag, tagIndex) => (
                              <span key={tagIndex} className="text-xs bg-muted/50 text-muted-foreground px-2 py-1 rounded-lg">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                  </div>
                  </motion.div>
                </motion.div>
              )
            })}
              </div>
          
          {filteredGuides.length === 0 && (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
              <div className="text-center space-y-3">
                <div className="mx-auto w-12 h-12 rounded-xl bg-muted/50 border border-border/30 flex items-center justify-center">
                  <BookOpen className="h-6 w-6 opacity-50" />
                </div>
                <p>No guides found matching your criteria</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

      {/* Enhanced Weather Effects Guide */}
      <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center space-x-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-purple/20 to-purple/10 border border-purple/30">
              <Cloud className="h-6 w-6 text-purple" />
            </div>
            <h3 className="card-title">Weather Effects</h3>
            </div>
          </div>
          <div className="card-content">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Object.entries(weatherEffects).map(([weatherType, effects], index) => (
              <motion.div
                key={weatherType}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="p-4 glass rounded-xl border border-border/30 transition-modern hover:bg-muted/20"
              >
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 rounded-lg bg-purple/10 border border-purple/30">
                    <Cloud className="h-4 w-4 text-purple" />
                  </div>
                  <h4 className="font-semibold capitalize">{weatherType}</h4>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{effects.description}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 rounded-lg bg-success/10 border border-success/30">
                    <div className="flex items-center space-x-1 mb-1">
                      <TrendingUp className="h-3 w-3 text-success" />
                      <span className="text-xs font-medium text-success">Growth</span>
                    </div>
                    <p className="text-sm font-bold text-success">
                      +{Math.round((effects.growth - 1) * 100)}%
                    </p>
                  </div>
                  <div className="p-2 rounded-lg bg-info/10 border border-info/30">
                    <div className="flex items-center space-x-1 mb-1">
                      <Zap className="h-3 w-3 text-info" />
                      <span className="text-xs font-medium text-info">Yield</span>
                    </div>
                    <p className="text-sm font-bold text-info">
                      +{Math.round((effects.yield - 1) * 100)}%
                    </p>
                  </div>
                </div>
              </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

      {/* Enhanced Rarity Guide */}
      <motion.div variants={itemVariants} className="gaming-card">
        <div className="card-header">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-orange/20 to-orange/10 border border-orange/30">
              <Star className="h-6 w-6 text-orange" />
            </div>
            <h3 className="card-title">Rarity System</h3>
          </div>
        </div>
        <div className="card-content">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Object.entries(rarityValues).map(([rarity, data], index) => (
              <motion.div
                key={rarity}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="p-4 glass rounded-xl border border-border/30 transition-modern hover:bg-muted/20"
              >
              <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 rounded-lg bg-orange/10 border border-orange/30">
                    <Star className="h-4 w-4 text-orange" />
                  </div>
                  <h4 className="font-semibold capitalize">{rarity}</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Base Value:</span>
                    <span className="font-bold">${data.baseValue}</span>
              </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Drop Rate:</span>
                    <span className="font-bold">{data.dropRate}%</span>
            </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Description:</span>
                    <span className="font-bold text-xs">{data.description}</span>
            </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
} 